import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { OwnerLayout } from '../../components/layouts/OwnerLayout';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { 
  Route, MapPin, Clock, User, Calendar, Navigation, Play, 
  RotateCcw, AlertCircle, CheckCircle2, Loader2, Car, ArrowRight
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';

const RouteOptimization = () => {
  const { api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [optimizing, setOptimizing] = useState(false);
  const [jobs, setJobs] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [selectedDate, setSelectedDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [selectedWorker, setSelectedWorker] = useState('all');
  const [optimizedRoute, setOptimizedRoute] = useState(null);
  const [startAddress, setStartAddress] = useState('');
  const [totalDistance, setTotalDistance] = useState(0);
  const [totalDuration, setTotalDuration] = useState(0);

  useEffect(() => {
    fetchData();
  }, [selectedDate, selectedWorker]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [jobsRes, workersRes] = await Promise.all([
        api.get('/jobs', { params: { date: selectedDate } }),
        api.get('/workers')
      ]);
      
      let filteredJobs = jobsRes.data.filter(j => 
        j.status !== 'completed' && j.status !== 'cancelled'
      );
      
      if (selectedWorker !== 'all') {
        filteredJobs = filteredJobs.filter(j => j.worker_id === selectedWorker);
      }
      
      setJobs(filteredJobs);
      setWorkers(workersRes.data);
      setOptimizedRoute(null);
    } catch (error) {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  // Geocode address using Nominatim
  const geocodeAddress = async (address) => {
    try {
      const params = new URLSearchParams({
        q: address,
        format: 'json',
        limit: '1',
        countrycodes: 'es'
      });
      
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?${params}`,
        {
          headers: {
            'Accept-Language': 'es',
            'User-Agent': 'AquaNexia/1.0'
          }
        }
      );
      
      const data = await response.json();
      if (data.length > 0) {
        return {
          lat: parseFloat(data[0].lat),
          lon: parseFloat(data[0].lon)
        };
      }
      return null;
    } catch (error) {
      console.error('Geocoding error:', error);
      return null;
    }
  };

  // Calculate distance between two points using Haversine formula
  const calculateDistance = (lat1, lon1, lat2, lon2) => {
    const R = 6371; // Earth's radius in km
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = 
      Math.sin(dLat/2) * Math.sin(dLat/2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
      Math.sin(dLon/2) * Math.sin(dLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return R * c;
  };

  // Nearest neighbor algorithm for route optimization
  const optimizeRouteNearestNeighbor = (locations, startIdx = 0) => {
    const n = locations.length;
    if (n <= 1) return locations.map((_, i) => i);
    
    const visited = new Array(n).fill(false);
    const route = [startIdx];
    visited[startIdx] = true;
    
    for (let i = 1; i < n; i++) {
      let nearestIdx = -1;
      let minDist = Infinity;
      const lastIdx = route[route.length - 1];
      
      for (let j = 0; j < n; j++) {
        if (!visited[j]) {
          const dist = calculateDistance(
            locations[lastIdx].lat, locations[lastIdx].lon,
            locations[j].lat, locations[j].lon
          );
          if (dist < minDist) {
            minDist = dist;
            nearestIdx = j;
          }
        }
      }
      
      if (nearestIdx !== -1) {
        route.push(nearestIdx);
        visited[nearestIdx] = true;
      }
    }
    
    return route;
  };

  const handleOptimize = async () => {
    if (jobs.length === 0) {
      toast.error('No hay trabajos para optimizar');
      return;
    }

    setOptimizing(true);
    try {
      // Geocode all addresses
      const geocodedJobs = await Promise.all(
        jobs.map(async (job) => {
          const coords = await geocodeAddress(job.client_address);
          return {
            ...job,
            coords
          };
        })
      );

      // Filter out jobs without coordinates
      const validJobs = geocodedJobs.filter(j => j.coords);
      
      if (validJobs.length === 0) {
        toast.error('No se pudieron geocodificar las direcciones');
        setOptimizing(false);
        return;
      }

      // Prepare locations for optimization
      let locations = validJobs.map(j => ({
        lat: j.coords.lat,
        lon: j.coords.lon
      }));

      // If start address provided, add it as first point
      let startIdx = 0;
      if (startAddress) {
        const startCoords = await geocodeAddress(startAddress);
        if (startCoords) {
          locations = [{ lat: startCoords.lat, lon: startCoords.lon }, ...locations];
          startIdx = 0;
        }
      }

      // Run optimization
      const optimizedOrder = optimizeRouteNearestNeighbor(locations, startIdx);
      
      // Calculate total distance and estimated time
      let totalDist = 0;
      for (let i = 0; i < optimizedOrder.length - 1; i++) {
        const from = locations[optimizedOrder[i]];
        const to = locations[optimizedOrder[i + 1]];
        totalDist += calculateDistance(from.lat, from.lon, to.lat, to.lon);
      }

      // Estimate time (avg 40 km/h in urban areas + 30 min per job)
      const drivingTime = (totalDist / 40) * 60; // minutes
      const workTime = validJobs.length * 30; // 30 min per job
      const totalTime = drivingTime + workTime;

      // Build optimized route
      const adjustedOrder = startAddress ? optimizedOrder.slice(1) : optimizedOrder;
      const optimizedJobs = adjustedOrder.map((idx, position) => {
        const jobIdx = startAddress ? idx - 1 : idx;
        return {
          ...validJobs[jobIdx],
          order: position + 1
        };
      });

      setOptimizedRoute(optimizedJobs);
      setTotalDistance(totalDist.toFixed(1));
      setTotalDuration(Math.round(totalTime));
      
      toast.success('Ruta optimizada correctamente');
    } catch (error) {
      console.error('Optimization error:', error);
      toast.error('Error al optimizar la ruta');
    } finally {
      setOptimizing(false);
    }
  };

  const openInGoogleMaps = () => {
    if (!optimizedRoute || optimizedRoute.length === 0) return;
    
    const waypoints = optimizedRoute
      .filter(job => job.coords)
      .map(job => `${job.coords.lat},${job.coords.lon}`)
      .join('/');
    
    const destination = optimizedRoute[optimizedRoute.length - 1];
    let url = `https://www.google.com/maps/dir`;
    
    if (startAddress) {
      url += `/${encodeURIComponent(startAddress)}`;
    }
    
    optimizedRoute.forEach(job => {
      if (job.coords) {
        url += `/${job.coords.lat},${job.coords.lon}`;
      }
    });
    
    window.open(url, '_blank');
  };

  const formatDuration = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours === 0) return `${mins} min`;
    return `${hours}h ${mins}min`;
  };

  if (loading) {
    return (
      <OwnerLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-sky-600"></div>
        </div>
      </OwnerLayout>
    );
  }

  return (
    <OwnerLayout>
      <div className="space-y-6" data-testid="route-optimization-page">
        {/* Header */}
        <div>
          <h1 className="font-display text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Route className="w-7 h-7 text-sky-600" />
            Optimización de Rutas
          </h1>
          <p className="text-slate-500">Planifica la ruta más eficiente para tus trabajos</p>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="p-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="space-y-2">
                <Label>Fecha</Label>
                <Input
                  type="date"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label>Trabajador</Label>
                <Select value={selectedWorker} onValueChange={setSelectedWorker}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos los trabajadores</SelectItem>
                    {workers.map(w => (
                      <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Punto de partida (opcional)</Label>
                <Input
                  placeholder="Ej: Calle Mayor 1, Madrid"
                  value={startAddress}
                  onChange={(e) => setStartAddress(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label>&nbsp;</Label>
                <Button 
                  onClick={handleOptimize} 
                  disabled={optimizing || jobs.length === 0}
                  className="w-full"
                >
                  {optimizing ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Optimizando...
                    </>
                  ) : (
                    <>
                      <Play className="w-4 h-4 mr-2" />
                      Optimizar ruta
                    </>
                  )}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Results */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Jobs list */}
          <div className="lg:col-span-2 space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center justify-between">
                  <span>
                    {optimizedRoute ? 'Ruta Optimizada' : 'Trabajos del día'}
                    <Badge className="ml-2" variant="outline">
                      {(optimizedRoute || jobs).length} paradas
                    </Badge>
                  </span>
                  {optimizedRoute && (
                    <Button variant="outline" size="sm" onClick={() => setOptimizedRoute(null)}>
                      <RotateCcw className="w-4 h-4 mr-1" />
                      Resetear
                    </Button>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {jobs.length === 0 ? (
                  <div className="text-center py-12">
                    <Calendar className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                    <p className="text-slate-500">No hay trabajos programados para esta fecha</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {(optimizedRoute || jobs).map((job, idx) => (
                      <div 
                        key={job.id} 
                        className={cn(
                          "p-4 rounded-lg border transition-colors",
                          optimizedRoute ? "bg-gradient-to-r from-sky-50 to-white border-sky-200" : "bg-slate-50 border-transparent"
                        )}
                      >
                        <div className="flex items-start gap-4">
                          {/* Order number */}
                          <div className={cn(
                            "w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0",
                            optimizedRoute 
                              ? "bg-sky-600 text-white" 
                              : "bg-slate-200 text-slate-600"
                          )}>
                            {optimizedRoute ? job.order : idx + 1}
                          </div>
                          
                          {/* Job details */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <h4 className="font-medium truncate">{job.title}</h4>
                              {job.is_emergency && (
                                <Badge className="bg-red-500 text-white text-xs">URGENTE</Badge>
                              )}
                            </div>
                            <div className="flex items-center gap-2 text-sm text-slate-600 mb-1">
                              <MapPin className="w-3 h-3 flex-shrink-0" />
                              <span className="truncate">{job.client_address || job.client_name}</span>
                            </div>
                            <div className="flex items-center gap-4 text-xs text-slate-500">
                              {job.scheduled_time && (
                                <span className="flex items-center gap-1">
                                  <Clock className="w-3 h-3" />
                                  {job.scheduled_time}
                                </span>
                              )}
                              {job.worker_name && (
                                <span className="flex items-center gap-1">
                                  <User className="w-3 h-3" />
                                  {job.worker_name}
                                </span>
                              )}
                            </div>
                          </div>
                          
                          {/* Arrow for optimized route */}
                          {optimizedRoute && idx < optimizedRoute.length - 1 && (
                            <ArrowRight className="w-5 h-5 text-sky-400 hidden sm:block" />
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Summary */}
          <div className="space-y-4">
            {optimizedRoute && (
              <Card className="bg-gradient-to-br from-sky-500 to-blue-600 text-white">
                <CardContent className="p-6">
                  <h3 className="font-semibold text-lg mb-4">Resumen de Ruta</h3>
                  <div className="space-y-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-white/20 flex items-center justify-center">
                        <Car className="w-5 h-5" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold">{totalDistance} km</p>
                        <p className="text-sm text-white/80">Distancia total</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-white/20 flex items-center justify-center">
                        <Clock className="w-5 h-5" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold">{formatDuration(totalDuration)}</p>
                        <p className="text-sm text-white/80">Tiempo estimado total</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-white/20 flex items-center justify-center">
                        <MapPin className="w-5 h-5" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold">{optimizedRoute.length}</p>
                        <p className="text-sm text-white/80">Paradas</p>
                      </div>
                    </div>
                  </div>
                  
                  <Button 
                    onClick={openInGoogleMaps}
                    className="w-full mt-6 bg-white text-sky-600 hover:bg-white/90"
                  >
                    <Navigation className="w-4 h-4 mr-2" />
                    Abrir en Google Maps
                  </Button>
                </CardContent>
              </Card>
            )}

            {/* Tips */}
            <Card>
              <CardHeader>
                <CardTitle className="text-sm flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 text-amber-500" />
                  Consejos
                </CardTitle>
              </CardHeader>
              <CardContent className="text-sm text-slate-600 space-y-2">
                <p>• Introduce tu punto de partida para una ruta más precisa</p>
                <p>• La optimización usa el algoritmo del vecino más cercano</p>
                <p>• El tiempo estimado incluye 30 min por trabajo</p>
                <p>• Puedes abrir la ruta directamente en Google Maps</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </OwnerLayout>
  );
};

export default RouteOptimization;
